/**
 * Everything the PDF report (/report-pdf) calculates from the report log.
 *
 * This file is shared, byte for byte, by
 *   ramus-app-cms  src/screens/report/pdf/reportPdfData.ts
 *   ramus-app-be   apps/ramus/src/modules/report-log/report-pdf/reportPdfData.ts
 * The CMS charts and tables call it on the logs they load, and the backend
 * calls it to send the PDF page ready made numbers instead of the logs, so
 * both always show the same thing. Change it in one repo, copy it to the
 * other; reportPdfData.sync test in both repos compares it with the copy
 * next door when both are checked out.
 *
 * It must stay free of imports and of formatting that depends on a library
 * or on the machine timezone: it returns numbers and timestamps, the page
 * formats them.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export type ReportLogs = Record<string, any>;

export interface ReportDevice {
  uniqueId?: string;
}

export interface ChartSchemaItem {
  logKey: string;
  dataKeys: string[];
  seriesOption: Record<string, any>;
}

export interface RunningLookup {
  origin: number;
  windowSeconds: number;
  values: Array<number | undefined>;
}

export interface ReportSchedule {
  startAt: number;
  endAt: number;
  interval: string;
}

/**
 * Raw table of a chart. The first row is the header: timestamps when
 * headerKind is 'bucket', day indexes from the schedule start when 'day',
 * hour indexes when 'hour'. The page turns it into labels.
 */
export interface ChartTableData {
  tables: any[][][];
  is48HourMode: boolean;
  columnCount: number;
  headerKind: 'bucket' | 'day' | 'hour';
  headerInterval: string;
}

export interface ChartSeriesData {
  timestamps: number[];
  dataSeries: any[];
}

/** One chart of the PDF page, ready to draw. */
export interface PdfChartData {
  schema: ChartSchemaItem[];
  table: ChartTableData;
  series: ChartSeriesData;
}

export enum EChartColor {
  black = 'rgb(0, 0, 0)',
  pink = 'rgb(243, 69, 132)',
  yellow = 'rgb(255, 162, 22)',
  yellowTransparent = 'rgba(255, 187, 0, 0.4)',
  teal = 'rgb(2, 204, 204)',
  tealTransparent = 'rgba(2, 204, 204, 0.6)',
  purple = 'rgb(111, 87, 233)',
  purpleTransparent = 'rgba(111, 87, 233, 0.6)',
  blue = 'rgb(0, 113, 241)',
  blueTransparent = 'rgba(0, 113, 241, 0.26)',
  blueDark = 'rgb(10, 28, 48)',
  green = 'rgb(3, 211, 131)',
}

const SECONDS_PER_HOUR = 3600;

/**
 * The value as the page sees it after the log went through JSON: NaN and
 * Infinity become null. A no-op on logs that came over HTTP, it keeps the
 * backend, which reads the logs before they are serialised, on the same
 * numbers.
 */
const asJson = (value: any) =>
  typeof value === 'number' && !Number.isFinite(value) ? null : value;

/* -------------------------------------------------------------------------- */
/* time buckets                                                                */
/* -------------------------------------------------------------------------- */

const roundTimestamp = (timestamp: number, type: 'minute' | 'hour') => {
  let interval = 60;
  if (type === 'hour') {
    interval = 3600;
  }
  return timestamp - (timestamp % interval);
};

function startOfDayInOffset(unixSeconds: number, timezone = 7) {
  const offsetSeconds = timezone * 3600;
  return (
    Math.floor((unixSeconds + offsetSeconds) / 86400) * 86400 - offsetSeconds
  );
}

function resolveContainerBounds(
  startTime: number,
  endTime: number,
  interval: string,
  timezone?: number,
): [number, number] {
  // the buckets of a day start at the local midnight
  if (interval === 'day') {
    return [
      startOfDayInOffset(startTime, timezone),
      startOfDayInOffset(endTime, timezone),
    ];
  }

  if (interval === 'hour') {
    return [roundTimestamp(startTime, 'hour'), roundTimestamp(endTime, 'hour')];
  }

  if (interval === 'minute') {
    return [
      roundTimestamp(startTime, 'minute'),
      roundTimestamp(endTime, 'minute'),
    ];
  }

  return [startTime, endTime];
}

// dayjs(unixSeconds * 1000).utcOffset(timezone * 60).startOf('month')
function startOfMonthInOffset(unixSeconds: number, timezone: number) {
  const offsetSeconds = timezone * SECONDS_PER_HOUR;
  const local = new Date((unixSeconds + offsetSeconds) * 1000);

  return (
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), 1) / 1000 -
    offsetSeconds
  );
}

function addOneMonthInOffset(monthStart: number, timezone: number) {
  const offsetSeconds = timezone * SECONDS_PER_HOUR;
  const local = new Date((monthStart + offsetSeconds) * 1000);

  return (
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth() + 1, 1) / 1000 -
    offsetSeconds
  );
}

export function generateTimestampData(params: {
  startTime: number;
  endTime: number;
  intervalInSecond?: number;
  interval: string;
  timezone?: number;
}): number[] {
  if (params.interval === 'month') {
    const timezone = params.timezone ?? 7;
    const timestampArray: number[] = [];
    let currentTimestamp = startOfMonthInOffset(params.startTime, timezone);
    const endTimestamp = startOfMonthInOffset(params.endTime, timezone);

    while (currentTimestamp <= endTimestamp) {
      timestampArray.push(currentTimestamp);
      currentTimestamp = addOneMonthInOffset(currentTimestamp, timezone);
    }

    return timestampArray;
  }

  const [startTime, endTime] = resolveContainerBounds(
    params.startTime,
    params.endTime,
    params.interval,
    params.timezone,
  );

  const currInterval = params.intervalInSecond || 60; // in seconds
  const timestampArray: number[] = [];
  let currentTimestamp = startTime;

  while (currentTimestamp <= endTime) {
    timestampArray.push(currentTimestamp);
    currentTimestamp += currInterval;
  }
  return timestampArray;
}

/**
 * Tells whether a log bucket belongs to the requested report range.
 */
export function createReportBucketFilter(params: {
  startTime: number;
  endTime: number;
  interval: string;
  timezone?: number;
}): (timestamp: number) => boolean {
  // The month container is a list of month starts, so membership there means
  // "aligned to a month", not "inside the range".
  if (params.interval === 'month') {
    const months = new Set(generateTimestampData(params));
    return (timestamp) => months.has(timestamp);
  }

  const [startTime, endTime] = resolveContainerBounds(
    params.startTime,
    params.endTime,
    params.interval,
    params.timezone,
  );

  return (timestamp) => timestamp >= startTime && timestamp <= endTime;
}

export function createBucketLookup<T>(
  section?: Record<string, T> | null,
): Map<number, T> {
  const lookup = new Map<number, T>();

  for (const key of Object.keys(section || {})) {
    lookup.set(Number(key), (section as Record<string, T>)[key]);
  }

  return lookup;
}

export function convertIntervalToSecond(interval: string) {
  let intervalInSecond = 0;

  if (interval === 'minute') {
    intervalInSecond = 60;
  } else if (interval === 'hour') {
    intervalInSecond = 60 * 60;
  } else if (interval === 'week') {
    intervalInSecond = 60 * 60 * 24 * 7;
  } else if (interval === 'month') {
    intervalInSecond = 60 * 60 * 24 * 30;
  } else {
    intervalInSecond = 60 * 60 * 24;
  }

  return intervalInSecond;
}

const dataIntervalOptions = [
  { value: 'minute', seconds: 60 },
  { value: 'hour', seconds: 3600 },
  { value: 'day', seconds: 86400 },
  { value: 'week', seconds: 604800 },
  { value: 'month', seconds: 2592000 },
];

function getSeconds(timeUnit: string) {
  let seconds = 0;
  dataIntervalOptions.forEach((option) => {
    if (option.value === timeUnit) {
      seconds = option.seconds;
    }
  });
  return seconds;
}

export function getAggregatedLength(dateParam: {
  startAt: number;
  endAt: number;
  interval: string;
}) {
  const secondsLength = dateParam.endAt - dateParam.startAt;
  let aggregatedLength = 1;

  const unitInSeconds = getSeconds(dateParam.interval);
  const expectedDataLength = secondsLength / unitInSeconds;
  const maxDataLimit = 50;

  if (expectedDataLength > maxDataLimit) {
    aggregatedLength = parseInt((expectedDataLength / maxDataLimit).toString());
  }
  return aggregatedLength;
}

export const toHHMM = (seconds: number) => {
  let hours: string | number = Math.floor(seconds / 3600);
  let minutes: string | number = Math.floor((seconds % 3600) / 60);

  hours = hours < 10 ? '0' + hours : hours;
  minutes = minutes < 10 ? '0' + minutes : minutes;

  return hours + ':' + minutes;
};

/* -------------------------------------------------------------------------- */
/* series                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * dataKeys of a fuel series: [FUEL_KEY, 'port'] for one flowmeter or AE,
 * [FUEL_KEY] for all of them. Read with bucketFuelLiter.
 */
export const FUEL_KEY = '@fuelConsLiter';

const BUCKET_TOTAL_KEYS = [
  'totalFuelCons',
  'totalFuelConsVolume',
  'totalFuelConsumption',
  'totalRunningTime',
];

/**
 * Liter of a flowmeter or AE (port, ae1, ...) in a bucket: stats.fuelConsLiter
 * that the backend calculates with the report summary, so a chart adds up to
 * the summary. A log without it (ramus2) uses the flow of the minutes of the
 * bucket, In - Out, or the growth of the inventory of an In alone.
 */
export function bucketFuelLiter(bucket: any, base: string): number | undefined {
  const inObj = bucket?.[`${base}In`];
  const outObj = bucket?.[`${base}Out`];
  const statObj = bucket?.[base];

  const withLiter = [inObj, statObj, outObj].find(
    (item) => typeof item?.stats?.fuelConsLiter === 'number',
  );
  if (withLiter) return asJson(withLiter.stats.fuelConsLiter);

  if (inObj && outObj) {
    // flow rate is liter per hour, one value per minute
    return (
      ((inObj.stats?.sumVolumeFlowrate || 0) -
        (outObj.stats?.sumVolumeFlowrate || 0)) /
      60
    );
  }
  if (inObj) return asJson(inObj.stats?.fuelConsVolume);
  if (statObj) return asJson(statObj.stats?.sumFuelCons);
  if (outObj) return asJson(outObj.stats?.fuelConsVolume);
  return undefined;
}

/** bucketFuelLiter of every flowmeter or AE of the bucket added up */
function bucketFuelTotal(bucket: any): number | undefined {
  if (!bucket) return undefined;

  const bases = new Set(
    Object.keys(bucket)
      .filter(
        (key) =>
          !BUCKET_TOTAL_KEYS.includes(key) &&
          bucket[key] &&
          typeof bucket[key] === 'object',
      )
      .map((key) => key.replace(/(In|Out)$/, '')),
  );
  let total: number | undefined;

  bases.forEach((base) => {
    const liter = bucketFuelLiter(bucket, base);
    if (typeof liter === 'number' && !Number.isNaN(liter)) {
      total = (total || 0) + liter;
    }
  });

  return total;
}

const isFuelKeys = (keyPath: string | string[]) =>
  Array.isArray(keyPath) && keyPath[0] === FUEL_KEY;

const getNestedValue = (obj: any, keyPath: string | string[]) => {
  if (typeof keyPath === 'string') {
    return obj[keyPath];
  }
  if (isFuelKeys(keyPath)) {
    return keyPath[1] ? bucketFuelLiter(obj, keyPath[1]) : bucketFuelTotal(obj);
  }
  return keyPath.reduce((prev, curr) => (prev ? prev[curr] : null), obj);
};

export function generateDataSeries(params: {
  timestamps: number[];
  key: string | string[];
  seriesOption: Record<string, any>;
  dataLog?: Record<string, any>;
}) {
  const data: any[] = [];
  let obj: any;

  const { timestamps, key, seriesOption, dataLog } = params;

  if (dataLog && Object.keys(dataLog).length) {
    obj = dataLog as any;
  }

  if (obj && Object.keys(obj).length) {
    const bucketLookup = createBucketLookup(obj);
    timestamps.forEach((timestamp) => {
      const bucket = bucketLookup.get(timestamp);
      if (bucket && key) {
        data.push(asJson(getNestedValue(bucket, key)));
      } else {
        data.push(null);
      }
    });
  }

  return {
    ...seriesOption,
    fillColor: seriesOption.fillColor || undefined,
    data: data,
    marker: seriesOption.marker || {
      enabled: false,
    },
    lineWidth: seriesOption.lineWidth || 2,
  };
}

export function generateDataSeriesTable(params: {
  timestamps: number[];
  key: string | string[];
  fm?: Record<string, any>;
  rpm?: Record<string, any>;
  gps?: Record<string, any>;
  ae?: Record<string, any>;
}) {
  const data: any[] = [];
  let obj: any;

  const { timestamps, key, fm, rpm, gps, ae } = params;

  if (fm && Object.keys(fm).length) {
    obj = fm;
  } else if (rpm && Object.keys(rpm).length) {
    obj = rpm;
  } else if (gps && Object.keys(gps).length) {
    obj = gps;
  } else if (ae && Object.keys(ae).length) {
    obj = ae;
  }

  const bucketLookup = createBucketLookup<any>(obj);

  timestamps.forEach((timestamp) => {
    const bucket = bucketLookup.get(timestamp);
    if (bucket && key) {
      data.push(asJson(getNestedValue(bucket, key)));
    } else {
      data.push(undefined);
    }
  });

  return data;
}

export function createRunningTimeAt(lookup?: RunningLookup | null) {
  if (!lookup) return undefined;

  return (timestamp: number) => {
    const index = Math.floor(
      (timestamp - lookup.origin) / lookup.windowSeconds,
    );
    if (index < 0 || index >= lookup.values.length) return null;
    const value = lookup.values[index];
    return typeof value === 'number' ? value : null;
  };
}

/**
 * The data part of ReportChart: the x axis buckets and each series with its
 * values, before they are normalised for Highcharts.
 */
export function computeReportChartSeries(params: {
  logs: ReportLogs;
  filter: {
    startAt: number;
    endAt: number;
    interval: string;
    timezone?: number;
  };
  chartSchema: ChartSchemaItem[];
  aggregatedLength?: number;
  sumRunningTime?: boolean;
  runningTimeAt?: (timestamp: number) => number | null;
}): ChartSeriesData {
  const {
    logs,
    filter,
    chartSchema,
    aggregatedLength,
    sumRunningTime = false,
    runningTimeAt,
  } = params;

  const intervalInSecond = convertIntervalToSecond(filter.interval);

  let timestamps = generateTimestampData({
    startTime: filter.startAt,
    endTime: filter.endAt,
    interval: filter.interval,
    intervalInSecond,
    timezone: filter.timezone ?? 7,
  });

  const dataSeries: any[] = [];
  const sourceTimestamps = timestamps;
  const step = aggregatedLength && aggregatedLength > 1 ? aggregatedLength : 1;

  if (step > 1) {
    const timestampAggregated = [];

    for (let i = 0; i < sourceTimestamps.length; i += step) {
      timestampAggregated.push(sourceTimestamps[i]);
    }

    timestamps = timestampAggregated;
  }

  chartSchema.forEach((schema) => {
    if ((logs as any)[schema.logKey]) {
      const isRunningTimeSeries = schema.dataKeys.some(
        (key) => key === 'runningTime' || key === 'totalRunningTime',
      );
      const isSpeedSeries = schema.dataKeys.includes('speed');
      // liters of the merged points are added up, whatever sumRunningTime
      const isFuelSeries = isFuelKeys(schema.dataKeys);
      const shouldAggregateWindow =
        step > 1 &&
        (isFuelSeries ||
          (sumRunningTime && (isRunningTimeSeries || isSpeedSeries)));

      if (runningTimeAt && isRunningTimeSeries) {
        const fullSeries = generateDataSeries({
          timestamps,
          key: schema.dataKeys,
          dataLog: (logs as any)[schema.logKey],
          seriesOption: schema.seriesOption,
        });
        dataSeries.push({
          ...fullSeries,
          data: timestamps.map((timestamp) => runningTimeAt(timestamp)),
        });
      } else if (shouldAggregateWindow) {
        const fullSeries = generateDataSeries({
          timestamps: sourceTimestamps,
          key: schema.dataKeys,
          dataLog: (logs as any)[schema.logKey],
          seriesOption: schema.seriesOption,
        });
        const aggregated: Array<number | null> = [];

        for (let i = 0; i < sourceTimestamps.length; i += step) {
          const window = fullSeries.data.slice(i, i + step);
          let total = 0;
          let found = 0;

          window.forEach((value: unknown) => {
            const numeric = typeof value === 'number' ? value : Number(value);
            if (Number.isFinite(numeric)) {
              total += numeric;
              found += 1;
            }
          });

          if (!found) {
            aggregated.push(null);
          } else {
            aggregated.push(isSpeedSeries ? total / found : total);
          }
        }

        dataSeries.push({ ...fullSeries, data: aggregated });
      } else {
        const res = generateDataSeries({
          timestamps,
          key: schema.dataKeys,
          dataLog: (logs as any)[schema.logKey],
          seriesOption: schema.seriesOption,
        });

        dataSeries.push(res);
      }
    }
  });

  return { timestamps, dataSeries };
}

/* -------------------------------------------------------------------------- */
/* devices                                                                     */
/* -------------------------------------------------------------------------- */

export interface DeviceValidation {
  hasAnyRPM: boolean;
  hasAnyClutch?: boolean;
  hasAnyFM: boolean;
  hasFMPort: boolean;
  hasRPMPort: boolean;
  hasFMStarboard: boolean;
  hasRPMStarboard: boolean;
  hasFMCenter: boolean;
  hasRPMCenter: boolean;
}

export function validateDeviceAvailability(
  devices: ReportDevice[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  assetType?: string,
): DeviceValidation {
  const hasDevice = (pattern: string) =>
    devices.some((device) => device.uniqueId?.includes(pattern));

  const hasAnyRPM = devices.some((device) => device.uniqueId?.includes('rpm-'));
  const hasAnyClutch = devices.some((device) =>
    device.uniqueId?.includes('clutch-'),
  );

  const hasAnyFM = devices.some((device) => device.uniqueId?.includes('fm-'));

  return {
    hasAnyRPM,
    hasAnyClutch,
    hasAnyFM,
    hasFMPort: hasDevice('fm-1'),
    hasRPMPort: hasDevice('rpm-1'),
    hasFMStarboard: hasDevice('fm-2'),
    hasRPMStarboard: hasDevice('rpm-2'),
    hasFMCenter: hasDevice('fm-3'),
    hasRPMCenter: hasDevice('rpm-3'),
  };
}

/**
 * The PDF page has no device list for some assets (a Ramus2 asset id is not
 * found by GET /asset/:id), the series to draw then follow the logs.
 */
function resolveDeviceValidation(params: {
  logs: ReportLogs;
  isReportSchedule: boolean;
  assetDevices?: ReportDevice[] | null;
  assetType?: string;
}): DeviceValidation {
  const { logs, isReportSchedule, assetDevices, assetType } = params;

  if (isReportSchedule && (!assetDevices || assetDevices.length === 0)) {
    const hasPortData =
      logs?.flowmeter &&
      Object.values(logs.flowmeter).some(
        (item: any) => item?.portIn?.stats?.fuelConsVolume !== undefined,
      );
    const hasStarboardData =
      logs?.flowmeter &&
      Object.values(logs.flowmeter).some(
        (item: any) => item?.starboardIn?.stats?.fuelConsVolume !== undefined,
      );
    const hasCenterData =
      logs?.flowmeter &&
      Object.values(logs.flowmeter).some(
        (item: any) => item?.centerIn?.stats?.fuelConsVolume !== undefined,
      );

    const hasRPMPort =
      logs?.rpm &&
      Object.values(logs.rpm).some(
        (item: any) => item?.port?.rpm !== undefined,
      );
    const hasRPMStarboard =
      logs?.rpm &&
      Object.values(logs.rpm).some(
        (item: any) => item?.starboard?.rpm !== undefined,
      );
    const hasRPMCenter =
      logs?.rpm &&
      Object.values(logs.rpm).some(
        (item: any) => item?.center?.rpm !== undefined,
      );

    return {
      hasAnyRPM: !!(hasRPMPort || hasRPMStarboard || hasRPMCenter),
      hasAnyFM: !!(hasPortData || hasStarboardData || hasCenterData),
      hasFMPort: !!hasPortData,
      hasRPMPort: !!hasRPMPort,
      hasFMStarboard: !!hasStarboardData,
      hasRPMStarboard: !!hasRPMStarboard,
      hasFMCenter: !!hasCenterData,
      hasRPMCenter: !!hasRPMCenter,
    };
  }

  return validateDeviceAvailability(assetDevices || [], assetType);
}

/* -------------------------------------------------------------------------- */
/* chart tables                                                                */
/* -------------------------------------------------------------------------- */

function readRunningTime(bucket: any, dataKeys: string[]) {
  if (isFuelKeys(dataKeys)) {
    const liter = asJson(getNestedValue(bucket, dataKeys));
    return typeof liter === 'number' && !Number.isNaN(liter)
      ? liter
      : undefined;
  }

  let current = bucket;
  for (const key of dataKeys) {
    if (current == null) return undefined;
    current = asJson(current[key]);
  }
  return typeof current === 'number' && !Number.isNaN(current)
    ? current
    : undefined;
}

export function sumRunningTimeByWindow(
  logs: Record<string, any>,
  dataKeys: string[],
  windowStarts: number[],
  windowSeconds: number,
) {
  const entries = Object.keys(logs)
    .map((key) => ({ ts: Number(key), bucket: logs[key] }))
    .filter((entry) => Number.isFinite(entry.ts))
    .sort((a, b) => a.ts - b.ts);

  let cursor = 0;

  return windowStarts.map((start, index) => {
    const end = windowStarts[index + 1] ?? start + windowSeconds;
    let sum = 0;
    let found = false;

    while (cursor < entries.length && entries[cursor].ts < start) {
      cursor += 1;
    }

    let probe = cursor;
    while (probe < entries.length && entries[probe].ts < end) {
      const value = readRunningTime(entries[probe].bucket, dataKeys);
      if (value !== undefined) {
        sum += value;
        found = true;
      }
      probe += 1;
    }

    return found ? sum : undefined;
  });
}

/** liters of the buckets in each column, rounded like the other columns */
function sumFuelByWindow(
  logs: Record<string, any>,
  dataKeys: string[],
  windowStarts: number[],
  windowSeconds: number,
) {
  return sumRunningTimeByWindow(
    logs,
    dataKeys,
    windowStarts,
    windowSeconds,
  ).map((value) =>
    value === undefined ? undefined : Number(value.toFixed(2)),
  );
}

function engineKeyPaths(aeLogs: Record<string, any>) {
  const first = Object.values(aeLogs)[0] as any;
  const labels =
    first && (first.ae1In || first.ae2In || first.ae3In)
      ? ['ae1In', 'ae2In', 'ae3In']
      : ['ae1', 'ae2', 'ae3'];
  return labels.map((label) => [label, 'runningTime']);
}

function sumEnginesByWindow(
  aeLogs: Record<string, any>,
  windowStarts: number[],
  windowSeconds: number,
) {
  const perEngine = engineKeyPaths(aeLogs).map((path) =>
    sumRunningTimeByWindow(aeLogs, path, windowStarts, windowSeconds),
  );

  return windowStarts.map((_, index) => {
    let sum = 0;
    let found = false;
    perEngine.forEach((values) => {
      const value = values[index];
      if (typeof value === 'number' && !Number.isNaN(value)) {
        sum += value;
        found = true;
      }
    });
    return found ? sum : undefined;
  });
}

/**
 * Columns of the table under a chart: hours for a day, 48 hours split in two
 * tables, days for a longer range, or the buckets themselves for day, week
 * and month intervals.
 */
function resolveTableLayout(
  schedule: Partial<ReportSchedule> | undefined,
  timezone: number,
) {
  // outside the PDF there is no schedule: no columns, as before
  const durationInSeconds =
    (schedule?.endAt as number) - (schedule?.startAt as number);
  const durationInDays = durationInSeconds / 86400;
  const scheduleInterval = schedule?.interval as string;
  const bucketIntervals = ['day', 'week', 'month'];
  const isBucketInterval = bucketIntervals.includes(scheduleInterval);

  let interval = 'hour';
  let columnCount = 24;
  let isDayMode = false;
  let is48HourMode = false;

  if (isBucketInterval) {
    interval = scheduleInterval;
  } else {
    if (durationInDays <= 1) {
      columnCount = 24;
    } else if (durationInDays <= 2) {
      columnCount = 48;
      is48HourMode = true;
    } else {
      columnCount = Math.ceil(durationInDays);
      isDayMode = true;
    }

    if (schedule?.interval === 'minute') {
      interval = 'hour';
    }
  }

  const intervalInSecond = convertIntervalToSecond(interval);

  const timestamps = generateTimestampData({
    startTime: schedule?.startAt as number,
    endTime: schedule?.endAt as number,
    interval,
    intervalInSecond,
    timezone,
  });

  if (isBucketInterval) {
    columnCount = timestamps.length;
  }

  const headerRow: number[] = isBucketInterval
    ? timestamps
    : Array.from({ length: columnCount }, (_, i) => i);

  return {
    interval,
    intervalInSecond,
    timestamps,
    columnCount,
    isDayMode,
    is48HourMode,
    isBucketInterval,
    headerRow,
  };
}

function aggregatePerDay(
  res: any[],
  columnCount: number,
  aggregate: (numericData: number[]) => number,
) {
  const aggregatedData: any[] = [];
  const dataPointsPerDay = 24;

  for (let day = 0; day < columnCount; day++) {
    const startIdx = day * dataPointsPerDay;
    const endIdx = Math.min(startIdx + dataPointsPerDay, res.length);
    const dayData = res.slice(startIdx, endIdx);
    const numericData = dayData.filter(
      (val) => typeof val === 'number' && val !== null && !Number.isNaN(val),
    );

    if (numericData.length > 0) {
      aggregatedData.push(aggregate(numericData));
    } else {
      aggregatedData.push('-');
    }
  }

  return aggregatedData;
}

function finishTable(
  dataSeriesTable: any[][],
  layout: ReturnType<typeof resolveTableLayout>,
): ChartTableData {
  const headerKind = layout.isBucketInterval
    ? 'bucket'
    : layout.isDayMode
    ? 'day'
    : 'hour';

  if (layout.is48HourMode) {
    const table1: any[] = [];
    const table2: any[] = [];

    dataSeriesTable.forEach((row, rowIdx) => {
      if (rowIdx === 0) {
        table1.push(row.slice(0, 24));
        table2.push(Array.from({ length: 24 }, (_, i) => i));
      } else {
        table1.push(row.slice(0, 24));
        table2.push(row.slice(24, 48));
      }
    });

    return {
      tables: [table1, table2],
      is48HourMode: true,
      columnCount: 24,
      headerKind,
      headerInterval: layout.interval,
    };
  }

  return {
    tables: [dataSeriesTable],
    is48HourMode: false,
    columnCount: layout.columnCount,
    headerKind,
    headerInterval: layout.interval,
  };
}

/* -------------------------------------------------------------------------- */
/* chart: RPM vs Fuel Cons. vs Speed vs AE Running Time                        */
/* -------------------------------------------------------------------------- */

export const RPM_FUEL_SPEED_Y_AXIS = ['fuel', 'rpm', 'speed', 'time'];

const RPM_FUEL_SPEED_SCHEMA: ChartSchemaItem[] = [
  {
    logKey: 'flowmeter',
    dataKeys: [FUEL_KEY],
    seriesOption: {
      name: 'Fuel Cons.',
      type: 'areaspline',
      yAxis: 1,
      zIndex: 1,
      marker: {
        enabled: false,
        fillColor: EChartColor.yellow,
      },
      fillColor: EChartColor.yellowTransparent,
      lineColor: EChartColor.yellow,
      color: EChartColor.yellowTransparent,
      tooltip: {
        valueSuffix: ' liter',
      },
    },
  },
  {
    logKey: 'rpm',
    dataKeys: ['port', 'rpm'],
    seriesOption: {
      name: 'Port RPM',
      type: 'column',
      yAxis: 0,
      zIndex: 3,
      marker: {
        enabled: false,
        fillColor: EChartColor.purpleTransparent,
      },
      lineColor: EChartColor.purple,
      color: EChartColor.purpleTransparent,
    },
  },
  {
    logKey: 'rpm',
    dataKeys: ['starboard', 'rpm'],
    seriesOption: {
      name: 'Starboard RPM',
      type: 'column',
      yAxis: 0,
      zIndex: 3,
      marker: {
        enabled: false,
        fillColor: EChartColor.tealTransparent,
      },
      lineColor: EChartColor.teal,
      color: EChartColor.tealTransparent,
    },
  },
  {
    logKey: 'rpm',
    dataKeys: ['center', 'rpm'],
    seriesOption: {
      name: 'Center RPM',
      type: 'column',
      yAxis: 0,
      zIndex: 3,
      marker: {
        enabled: false,
        fillColor: EChartColor.blueTransparent,
      },
      lineColor: EChartColor.blue,
      color: EChartColor.blueTransparent,
    },
  },
  {
    logKey: 'gps',
    dataKeys: ['speed'],
    seriesOption: {
      name: 'Speed',
      type: 'spline',
      yAxis: 2,
      zIndex: 4,
      marker: {
        enabled: false,
        fillColor: EChartColor.pink,
      },
      lineColor: EChartColor.pink,
      color: EChartColor.pink,
      tooltip: {
        valueSuffix: ' KNOT',
      },
    },
  },
  {
    logKey: 'ae',
    dataKeys: ['totalRunningTime'],
    seriesOption: {
      name: 'AE Running Time',
      type: 'spline',
      yAxis: 3,
      zIndex: 4,
      marker: {
        enabled: false,
        fillColor: EChartColor.black,
      },
      lineColor: EChartColor.black,
      color: EChartColor.black,
      tooltip: {
        valueSuffix: ' hour',
      },
    },
  },
];

export function buildRpmFuelSpeedSchema(params: {
  logs: ReportLogs;
  isReportSchedule: boolean;
  assetDevices?: ReportDevice[] | null;
  assetType?: string;
  isEMS?: boolean;
  activeChartType: string;
}): ChartSchemaItem[] {
  const { isEMS, activeChartType } = params;
  const deviceValidation = resolveDeviceValidation(params);

  let currentSchema = RPM_FUEL_SPEED_SCHEMA;
  if (isEMS) {
    currentSchema = currentSchema.filter((item) => item.logKey !== 'flowmeter');
  }

  currentSchema = currentSchema.filter((item) => {
    if (item.logKey === 'rpm') {
      if (item.dataKeys.includes('port')) {
        return deviceValidation.hasRPMPort;
      }
      if (item.dataKeys.includes('starboard')) {
        return deviceValidation.hasRPMStarboard;
      }
      if (item.dataKeys.includes('center')) {
        return deviceValidation.hasRPMCenter;
      }
    }
    return true;
  });

  currentSchema = currentSchema.map((item) => {
    if (item.logKey === 'rpm') {
      return {
        ...item,
        seriesOption: {
          ...(item.seriesOption as any),
          type: activeChartType === 'line' ? 'spline' : 'column',
        },
      };
    } else {
      return item;
    }
  });

  return currentSchema;
}

export function buildRpmFuelSpeedTable(params: {
  logs: ReportLogs;
  schema: ChartSchemaItem[];
  schedule?: Partial<ReportSchedule>;
  timezone: number;
}): ChartTableData & { aeLookup: RunningLookup | null } {
  const { logs, schema, schedule } = params;
  const layout = resolveTableLayout(schedule, params.timezone);
  const { timestamps, intervalInSecond, columnCount, isDayMode } = layout;

  const dataSeriesTable: any[] = [layout.headerRow];
  let aeLookup: RunningLookup | null = null;

  schema.forEach((data) => {
    if ((logs as any)[data.logKey]) {
      const isAeRunningTime = data.logKey === 'ae';
      const isFuel = isFuelKeys(data.dataKeys);
      const res = isAeRunningTime
        ? sumEnginesByWindow(
            (logs as any)[data.logKey],
            timestamps,
            intervalInSecond,
          )
        : isFuel
        ? sumFuelByWindow(
            (logs as any)[data.logKey],
            data.dataKeys,
            timestamps,
            intervalInSecond,
          )
        : generateDataSeriesTable({
            timestamps,
            key: data.dataKeys,
            fm: (logs as any)[data.logKey],
          });

      let processedRes: any[] = res;

      if (isDayMode) {
        processedRes = aggregatePerDay(res, columnCount, (numericData) => {
          const total = numericData.reduce((sum, val) => sum + val, 0);
          if (isFuel) return Number(total.toFixed(2));
          return isAeRunningTime
            ? total
            : Number((total / numericData.length).toFixed(2));
        });
      }

      const paddedRes = [...processedRes];
      while (paddedRes.length < columnCount) {
        paddedRes.push('-');
      }

      if (data.logKey === 'ae') {
        aeLookup = {
          origin: timestamps[0] ?? schedule?.startAt ?? 0,
          windowSeconds: isDayMode ? 86400 : intervalInSecond,
          values: paddedRes.map((item) =>
            typeof item === 'number' ? item : undefined,
          ),
        };
        dataSeriesTable.push(
          paddedRes.map((item) =>
            typeof item === 'number' ? toHHMM(item) : item,
          ),
        );
      } else {
        dataSeriesTable.push(paddedRes);
      }
    }
  });

  return { ...finishTable(dataSeriesTable, layout), aeLookup };
}

/* -------------------------------------------------------------------------- */
/* chart: fuel consumption (ME, port, starboard, center)                       */
/* -------------------------------------------------------------------------- */

export function buildFuelConsSchema(params: {
  logs: ReportLogs;
  chartType: string;
  activeChartType: string;
  isReportSchedule: boolean;
  assetDevices?: ReportDevice[] | null;
  assetType?: string;
}): ChartSchemaItem[] {
  const { chartType, activeChartType } = params;
  const deviceValidation = resolveDeviceValidation(params);

  const currentSchema = [
    {
      logKey: 'flowmeter',
      dataKeys: [FUEL_KEY, 'port'],
      seriesOption: {
        name: 'Port Fuel Cons.',
        type: 'column',
        yAxis: 1,
        zIndex: 1,
        marker: {
          enabled: false,
          fillColor: EChartColor.yellow,
        },
        lineColor: EChartColor.yellow,
        color: chartType.includes('me')
          ? EChartColor.yellow
          : EChartColor.yellowTransparent,
        fillColor: EChartColor.yellowTransparent,
        tooltip: {
          valueSuffix: ' liter',
        },
      },
    },
    {
      logKey: 'flowmeter',
      dataKeys: [FUEL_KEY, 'starboard'],
      seriesOption: {
        name: 'Starboard Fuel Cons.',
        type: 'column',
        yAxis: 1,
        zIndex: 1,
        marker: {
          enabled: false,
          fillColor: EChartColor.yellow,
        },
        lineColor: EChartColor.yellow,
        color: chartType.includes('me')
          ? EChartColor.yellow
          : EChartColor.yellowTransparent,
        fillColor: EChartColor.yellowTransparent,
        tooltip: {
          valueSuffix: ' liter',
        },
      },
    },
    {
      logKey: 'flowmeter',
      dataKeys: [FUEL_KEY, 'center'],
      seriesOption: {
        name: 'Center Fuel Cons.',
        type: 'column',
        yAxis: 1,
        zIndex: 1,
        marker: {
          enabled: false,
          fillColor: EChartColor.yellow,
        },
        lineColor: EChartColor.yellow,
        color: EChartColor.yellowTransparent,
        tooltip: {
          valueSuffix: ' liter',
        },
      },
    },
    {
      logKey: 'rpm',
      dataKeys: ['port', 'rpm'],
      seriesOption: {
        name: 'Port RPM',
        type: 'spline',
        yAxis: 0,
        zIndex: 3,
        marker: {
          enabled: false,
          fillColor: EChartColor.purpleTransparent,
        },
        lineColor: EChartColor.purple,
        color: EChartColor.purpleTransparent,
      },
    },
    {
      logKey: 'rpm',
      dataKeys: ['starboard', 'rpm'],
      seriesOption: {
        name: 'Starboard RPM',
        type: 'spline',
        yAxis: 0,
        zIndex: 3,
        marker: {
          enabled: false,
          fillColor: EChartColor.tealTransparent,
        },
        lineColor: EChartColor.teal,
        color: EChartColor.tealTransparent,
      },
    },
    {
      logKey: 'rpm',
      dataKeys: ['center', 'rpm'],
      seriesOption: {
        name: 'Center RPM',
        type: 'spline',
        yAxis: 0,
        zIndex: 3,
        marker: {
          enabled: false,
          fillColor: EChartColor.blueTransparent,
        },
        lineColor: EChartColor.blue,
        color: EChartColor.blueTransparent,
      },
    },
    {
      logKey: 'gps',
      dataKeys: ['speed'],
      seriesOption: {
        name: 'Speed',
        type: 'spline',
        yAxis: 2,
        zIndex: 4,
        marker: {
          enabled: false,
          fillColor: EChartColor.pink,
        },
        lineColor: EChartColor.pink,
        color: EChartColor.pink,
        tooltip: {
          valueSuffix: ' KNOT',
        },
      },
    },
    {
      logKey: 'rpm',
      dataKeys: ['port', 'runningTime'],
      seriesOption: {
        name: 'Port Run. Time',
        type: 'spline',
        yAxis: 2,
        zIndex: 3,
        marker: {
          enabled: false,
          fillColor: EChartColor.black,
        },
        lineColor: EChartColor.black,
        color: EChartColor.black,
      },
    },
    {
      logKey: 'rpm',
      dataKeys: ['starboard', 'runningTime'],
      seriesOption: {
        name: 'Starboard Run. Time',
        type: 'spline',
        yAxis: 2,
        zIndex: 3,
        marker: {
          enabled: false,
          fillColor: EChartColor.black,
        },
        lineColor: EChartColor.black,
        color: EChartColor.black,
      },
    },
    {
      logKey: 'rpm',
      dataKeys: ['center', 'runningTime'],
      seriesOption: {
        name: 'Center Run. Time',
        type: 'spline',
        yAxis: 2,
        zIndex: 3,
        marker: {
          enabled: false,
          fillColor: EChartColor.black,
        },
        lineColor: EChartColor.black,
        color: EChartColor.black,
      },
    },
  ]
    .filter((item) => {
      if (chartType !== 'me') {
        return item.seriesOption.name.toLowerCase().includes(chartType);
      } else {
        return !item.seriesOption.name.toLowerCase().includes('time');
      }
    })
    .filter((item) => {
      const name = item.seriesOption.name.toLowerCase();

      if (name.includes('port') && name.includes('fuel')) {
        return deviceValidation.hasFMPort;
      }
      if (name.includes('starboard') && name.includes('fuel')) {
        return deviceValidation.hasFMStarboard;
      }
      if (name.includes('center') && name.includes('fuel')) {
        return deviceValidation.hasFMCenter;
      }

      if (name.includes('port') && name.includes('rpm')) {
        return deviceValidation.hasRPMPort;
      }
      if (name.includes('starboard') && name.includes('rpm')) {
        return deviceValidation.hasRPMStarboard;
      }
      if (name.includes('center') && name.includes('rpm')) {
        return deviceValidation.hasRPMCenter;
      }

      return true;
    })
    .map((item) => {
      if (chartType === 'me') {
        if (item.logKey === 'flowmeter') {
          if (item.dataKeys.includes('port')) {
            return {
              ...item,
              seriesOption: {
                ...item.seriesOption,
                type: 'spline',
                marker: {
                  enabled: false,
                  fillColor: EChartColor.yellow,
                },
                lineColor: EChartColor.yellow,
                color: chartType.includes('me')
                  ? EChartColor.yellow
                  : EChartColor.yellowTransparent,
                fillColor: EChartColor.yellowTransparent,
              },
            };
          } else if (item.dataKeys.includes('starboard')) {
            return {
              ...item,
              seriesOption: {
                ...item.seriesOption,
                type: 'spline',
                marker: {
                  enabled: false,
                  fillColor: EChartColor.black,
                },
                lineColor: EChartColor.black,
                color: chartType.includes('me')
                  ? EChartColor.black
                  : EChartColor.black,
                fillColor: EChartColor.black,
              },
            };
          } else if (item.dataKeys.includes('center')) {
            return {
              ...item,
              seriesOption: {
                ...item.seriesOption,
                type: 'spline',
                marker: {
                  enabled: false,
                  fillColor: EChartColor.green,
                },
                lineColor: EChartColor.green,
                color: chartType.includes('me')
                  ? EChartColor.green
                  : EChartColor.green,
                fillColor: EChartColor.green,
              },
            };
          } else {
            return item;
          }
        } else if (item.logKey === 'rpm') {
          if (item.dataKeys.includes('port')) {
            return {
              ...item,
              seriesOption: {
                ...item.seriesOption,
                type: activeChartType === 'line' ? 'spline' : 'column',
                marker: {
                  enabled: false,
                  fillColor: EChartColor.purple,
                },
                zIndex: 0,
                lineColor: EChartColor.purple,
                color: chartType.includes('me')
                  ? EChartColor.purple
                  : EChartColor.purpleTransparent,
                fillColor: EChartColor.purpleTransparent,
              },
            };
          } else if (item.dataKeys.includes('starboard')) {
            return {
              ...item,
              seriesOption: {
                ...item.seriesOption,
                type: activeChartType === 'line' ? 'spline' : 'column',
                marker: {
                  enabled: false,
                  fillColor: EChartColor.teal,
                },
                zIndex: 0,
                lineColor: EChartColor.teal,
                color: chartType.includes('me')
                  ? EChartColor.teal
                  : EChartColor.tealTransparent,
                fillColor: EChartColor.tealTransparent,
              },
            };
          } else if (item.dataKeys.includes('center')) {
            return {
              ...item,
              seriesOption: {
                ...item.seriesOption,
                type: activeChartType === 'line' ? 'spline' : 'column',
                marker: {
                  enabled: false,
                  fillColor: EChartColor.green,
                },
                zIndex: 0,
                lineColor: EChartColor.green,
                color: chartType.includes('me')
                  ? EChartColor.green
                  : EChartColor.green,
                fillColor: EChartColor.green,
              },
            };
          } else {
            return item;
          }
        } else {
          return item;
        }
      } else if (item.logKey === 'flowmeter') {
        return {
          ...item,
          seriesOption: {
            ...(item.seriesOption as any),
            type:
              activeChartType === 'line'
                ? 'areaspline'
                : item.seriesOption.type,
          },
        };
      } else {
        return item;
      }
    });

  return currentSchema;
}

export function buildFuelConsTable(params: {
  logs: ReportLogs;
  schema: ChartSchemaItem[];
  schedule?: Partial<ReportSchedule>;
  timezone: number;
}): ChartTableData & { runningLookup: RunningLookup | null } {
  const { logs, schema, schedule } = params;
  const layout = resolveTableLayout(schedule, params.timezone);
  const { timestamps, intervalInSecond, columnCount, isDayMode } = layout;

  const dataSeriesTable: any[] = [layout.headerRow];
  let runningLookup: RunningLookup | null = null;

  schema.forEach((schemaItem) => {
    if ((logs as any)[schemaItem.logKey]) {
      const isRunningTime = schemaItem.dataKeys.includes('runningTime');
      const isFuel = isFuelKeys(schemaItem.dataKeys);
      const res = isRunningTime
        ? sumRunningTimeByWindow(
            (logs as any)[schemaItem.logKey],
            schemaItem.dataKeys,
            timestamps,
            intervalInSecond,
          )
        : isFuel
        ? sumFuelByWindow(
            (logs as any)[schemaItem.logKey],
            schemaItem.dataKeys,
            timestamps,
            intervalInSecond,
          )
        : generateDataSeriesTable({
            timestamps,
            key: schemaItem.dataKeys,
            fm: (logs as any)[schemaItem.logKey],
          });

      let processedRes: any[] = res;

      if (isDayMode) {
        processedRes = aggregatePerDay(res, columnCount, (numericData) => {
          const total = numericData.reduce((sum, val) => sum + val, 0);
          if (isFuel) return Number(total.toFixed(2));
          return isRunningTime
            ? total
            : Number((total / numericData.length).toFixed(2));
        });
      }

      const paddedRes = [...processedRes];
      while (paddedRes.length < columnCount) {
        paddedRes.push('-');
      }

      if (schemaItem.dataKeys.includes('runningTime')) {
        runningLookup = {
          origin: timestamps[0] ?? schedule?.startAt ?? 0,
          windowSeconds: isDayMode ? 86400 : intervalInSecond,
          values: paddedRes.map((item) =>
            typeof item === 'number' ? item : undefined,
          ),
        };
        dataSeriesTable.push(
          paddedRes.map((item) =>
            typeof item === 'number' ? toHHMM(item) : item,
          ),
        );
      } else {
        dataSeriesTable.push(paddedRes);
      }
    }
  });

  return { ...finishTable(dataSeriesTable, layout), runningLookup };
}

/* -------------------------------------------------------------------------- */
/* chart: AE running time                                                      */
/* -------------------------------------------------------------------------- */

export const AE_RUNNING_TIME_Y_AXIS = ['time', 'fuel'];

const aeRunningTimeSeries = (labels: string[]): ChartSchemaItem[] => [
  {
    logKey: 'ae',
    dataKeys: [labels[0], 'runningTime'],
    seriesOption: {
      name: 'AE 1 Run. Time',
      type: 'column',
      yAxis: 1,
      zIndex: 0,
      marker: {
        enabled: false,
        fillColor: EChartColor.teal,
      },
      fillColor: EChartColor.teal,
      lineColor: EChartColor.teal,
      tooltip: {
        valueSuffix: ' hour',
      },
    },
  },
  {
    logKey: 'ae',
    dataKeys: [labels[1], 'runningTime'],
    seriesOption: {
      name: 'AE 2 Run. Time',
      type: 'column',
      yAxis: 1,
      zIndex: 1,
      marker: {
        enabled: false,
        fillColor: EChartColor.purple,
      },
      lineColor: EChartColor.purple,
      tooltip: {
        valueSuffix: ' hour',
      },
    },
  },
  {
    logKey: 'ae',
    dataKeys: [labels[2], 'runningTime'],
    seriesOption: {
      name: 'AE 3 Run. Time',
      type: 'column',
      yAxis: 1,
      zIndex: 1,
      marker: {
        enabled: false,
        fillColor: EChartColor.yellow,
      },
      lineColor: EChartColor.yellow,
      tooltip: {
        valueSuffix: ' hour',
      },
    },
  },
  {
    logKey: 'ae',
    dataKeys: ['avgFuelCons'],
    seriesOption: {
      name: 'Avg. Fuel Cons',
      type: 'spline',
      yAxis: 0,
      zIndex: 2,
      marker: {
        enabled: false,
        fillColor: EChartColor.yellow,
      },
      lineColor: EChartColor.yellow,
      color: EChartColor.yellow,
      tooltip: {
        valueSuffix: ' liter/hour',
      },
    },
  },
];

const AE_SCHEMA = aeRunningTimeSeries(['ae1', 'ae2', 'ae3']);
const AE_SCHEMA_CUSTOM = aeRunningTimeSeries(['ae1In', 'ae2In', 'ae3In']);

/**
 * Adds the average AE fuel consumption over the whole range to every AE
 * bucket, it is drawn as a flat line: per AE the liters of the range over its
 * running hours (liter per running hour, the same for every interval), the
 * AEs added up. totalAE1..3 count the buckets an AE used fuel in.
 */
export function enhanceAeLogs(logs: ReportLogs): ReportLogs {
  if (!logs || !logs.ae) return logs;

  const engines = ['ae1', 'ae2', 'ae3'].map((base) => ({
    base,
    liters: 0,
    runningSeconds: 0,
    buckets: 0,
  }));
  const enhancedAe: any = {};

  Object.entries(logs.ae).forEach(([timestamp, aeData]: [string, any]) => {
    engines.forEach((engine) => {
      const liter = bucketFuelLiter(aeData, engine.base);
      const label = aeData?.[`${engine.base}In`] || aeData?.[engine.base];
      const running = asJson(label?.runningTime);

      if (typeof liter === 'number' && !Number.isNaN(liter)) {
        engine.liters += liter;
        if (liter > 0) engine.buckets += 1;
      }
      if (typeof running === 'number' && !Number.isNaN(running)) {
        engine.runningSeconds += running;
      }
    });
    enhancedAe[timestamp] = aeData;
  });

  const avgFuelCons = engines.reduce(
    (sum, engine) =>
      engine.runningSeconds > 0
        ? sum + engine.liters / (engine.runningSeconds / SECONDS_PER_HOUR)
        : sum,
    0,
  );
  const [totalAE1, totalAE2, totalAE3] = engines.map(
    (engine) => engine.buckets,
  );

  Object.keys(enhancedAe).forEach(
    (timestamp) =>
      (enhancedAe[timestamp] = {
        ...enhancedAe[timestamp],
        avgFuelCons,
        totalAE1,
        totalAE2,
        totalAE3,
      }),
  );

  return { ...logs, ae: enhancedAe };
}

export function selectAeSchema(enhancedLogs: ReportLogs): ChartSchemaItem[] {
  if (enhancedLogs && enhancedLogs.ae) {
    let dataAE: any = undefined;

    if (Object.values(enhancedLogs.ae).length > 0) {
      const firstLog = Object.values(enhancedLogs.ae)[0];
      dataAE = firstLog;
    }

    if (dataAE && (dataAE['ae1In'] || dataAE['ae2In'] || dataAE['ae3In'])) {
      return AE_SCHEMA_CUSTOM;
    }

    return AE_SCHEMA;
  }

  return AE_SCHEMA;
}

export function buildAeRunningTimeTable(params: {
  enhancedLogs: ReportLogs;
  schema: ChartSchemaItem[];
  schedule?: Partial<ReportSchedule>;
  timezone: number;
}): ChartTableData {
  const { enhancedLogs, schema: dataSchema, schedule } = params;
  const layout = resolveTableLayout(schedule, params.timezone);
  const { timestamps, intervalInSecond, columnCount, isDayMode } = layout;

  const dataSeriesTable: any[] = [layout.headerRow];

  dataSchema.forEach((schemaItem) => {
    if ((enhancedLogs as any)?.[schemaItem.logKey]) {
      if (schemaItem.dataKeys.includes('avgFuelCons')) {
        return;
      }
      const res = sumRunningTimeByWindow(
        (enhancedLogs as any)[schemaItem.logKey],
        schemaItem.dataKeys,
        timestamps,
        intervalInSecond,
      );

      let processedRes: Array<number | string | undefined> = res;

      if (isDayMode) {
        processedRes = aggregatePerDay(res, columnCount, (numericData) =>
          numericData.reduce((sum, val) => sum + val, 0),
        );
      }

      const paddedRes = [...processedRes];
      while (paddedRes.length < columnCount) {
        paddedRes.push('-');
      }

      dataSeriesTable.push(
        paddedRes.map((item) =>
          typeof item === 'number' ? toHHMM(item) : item,
        ),
      );
    }
  });

  return finishTable(dataSeriesTable, layout);
}

/* -------------------------------------------------------------------------- */
/* operation mode                                                              */
/* -------------------------------------------------------------------------- */

export interface OperationModeDetail {
  name?: string | null;
  rpmMin?: string | number | null;
  rpmMax?: string | number | null;
  clutchMax?: string | number | null;
  speed?: string | number | null;
  speedOperator?: string | null;
}

export interface OperationModeSummaryRow {
  name: string;
  runningTime: number;
  consRate: number;
}

export interface OperationModeSections {
  engineKeys: string[];
  clutchEngines: string[];
  summaryRows: OperationModeSummaryRow[];
  detailRows: Array<
    OperationModeSummaryRow & { rpmRange: string; speed: string }
  >;
  perEngineRows: Record<string, OperationModeSummaryRow[]>;
  perEngineHasData: boolean;
  clutchSummaryRows: OperationModeSummaryRow[];
  clutchDetailRows: Array<OperationModeSummaryRow & { clutchRange: string }>;
  perEngineClutchRows: Record<string, OperationModeSummaryRow[]>;
}

export const CLUTCH_OPERATION_MODE_KEYS = [
  'clutchPort',
  'clutchStarboard',
  'clutchCenter',
] as const;

const engineOrder = ['starboard', 'port', 'center'];

const clutchKeyByEngine: Record<string, string> = {
  port: 'clutchPort',
  starboard: 'clutchStarboard',
  center: 'clutchCenter',
};

/** Devices whose operation mode settings the page loads. */
export function collectOperationModeDeviceIds(
  operationMode?: Record<string, any> | null,
): string[] {
  const devicesIds = new Set<string>();
  Object.values(operationMode || {}).forEach((bucket: any) => {
    ['port', 'starboard', 'center', ...CLUTCH_OPERATION_MODE_KEYS].forEach(
      (key) => {
        const deviceUniqueId = bucket?.[key]?.deviceUniqueId;
        if (deviceUniqueId) devicesIds.add(deviceUniqueId);
      },
    );
  });
  return Array.from(devicesIds);
}

/** Splits the operation mode settings into engine and clutch thresholds. */
export function splitOperationModeDetails(
  results: Array<{
    deviceId?: string | null;
    operationModeDetails?: OperationModeDetail[] | null;
  }>,
) {
  const engineDetails: OperationModeDetail[] = [];
  const clutchDetails: OperationModeDetail[] = [];
  results.forEach((item) => {
    const target = (item.deviceId || '').toLowerCase().includes('clutch')
      ? clutchDetails
      : engineDetails;
    item.operationModeDetails?.forEach((detail) => {
      target.push(detail);
    });
  });
  return { engineDetails, clutchDetails };
}

/**
 * The modes of an engine in a bucket: an hour or a day bucket carries the
 * breakdown of the modes of its minutes, a minute bucket its own mode.
 */
export function operationModeEntries(data: any): Array<{
  mode: string;
  runningTime: unknown;
  consRate: unknown;
  rpmMin?: unknown;
  rpmMax?: unknown;
  speed?: unknown;
  speedOperator?: unknown;
}> {
  if (!data) return [];
  if (Array.isArray(data.modes)) {
    return data.modes.map((item: any) => ({
      mode: item.name,
      runningTime: item.runningTime,
      consRate: item.consRate,
      rpmMin: item.rpmMin,
      rpmMax: item.rpmMax,
      speed: item.speed,
      speedOperator: item.speedOperator,
    }));
  }
  return [data];
}

export function buildOperationModeSections(params: {
  operationMode?: Record<string, any> | null;
  opModes: OperationModeDetail[];
  clutchOpModes: OperationModeDetail[];
  range: { startAt: number; endAt: number; interval: string };
  timezone?: number;
}): OperationModeSections {
  const { opModes, clutchOpModes, range, timezone } = params;
  const operationMode = params.operationMode;

  const isInsideRange = createReportBucketFilter({
    startTime: range.startAt,
    endTime: range.endAt,
    interval: range.interval,
    timezone,
  });

  const engineKeys = (() => {
    const engines = new Set<string>();
    if (operationMode) {
      Object.keys(operationMode).forEach((key) => {
        if (!isInsideRange(Number(key))) return;
        const entry = operationMode?.[key];
        ['port', 'starboard', 'center'].forEach((engine) => {
          if (entry?.[engine]) {
            engines.add(engine);
          }
        });
      });
    }
    return Array.from(engines);
  })();

  const clutchEngines = (() => {
    const engines = new Set<string>();
    if (operationMode) {
      Object.keys(operationMode).forEach((key) => {
        if (!isInsideRange(Number(key))) return;
        const entry = operationMode?.[key];
        engineOrder.forEach((engine) => {
          if (entry?.[clutchKeyByEngine[engine]]) {
            engines.add(engine);
          }
        });
      });
    }
    return engineOrder.filter((engine) => engines.has(engine));
  })();

  const engineCount = engineKeys.length || 1;

  const isEmptyValue = (value: unknown) =>
    value === null || value === undefined || value === '';
  const toFiniteOrNull = (value: unknown): number | null => {
    if (isEmptyValue(value)) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };
  /** Same key for a tier of the settings and for the tier a minute matched. */
  const speedText = (speed: unknown, operator: unknown) => {
    const value = toFiniteOrNull(speed);
    if (value === null) return '-';
    const op = String(operator ?? '').trim();
    // a missing operator keeps the legacy speed ceiling
    return `${op || '<='} ${value} kn`;
  };
  const tierKey = (
    name: unknown,
    rpmMin: unknown,
    rpmMax: unknown,
    speed?: unknown,
    speedOperator?: unknown,
  ) =>
    `${String(name || '').trim().toLowerCase()}__${toFiniteOrNull(rpmMin) ?? ''}__${toFiniteOrNull(rpmMax) ?? ''}__${speedText(speed, speedOperator)}`;

  const opModeDetails = (() => {
    const unique = new Map<
      string,
      {
        name: string;
        rpmMax: number;
        rpmMin: number | null;
        speed: string;
        key: string;
      }
    >();
    opModes.forEach((item) => {
      const name = String(item.name || '').trim();
      if (!name) return;
      const rawThreshold = !isEmptyValue(item.clutchMax)
        ? item.clutchMax
        : item.rpmMax;
      const rpmMin = toFiniteOrNull(item.rpmMin);
      // a tier with a minimum and no maximum is open, shown as 1140<
      const rpmMax = isEmptyValue(rawThreshold)
        ? rpmMin !== null
          ? Number.POSITIVE_INFINITY
          : Number.NaN
        : Number(rawThreshold);
      if (Number.isNaN(rpmMax)) return;
      const key = tierKey(
        name,
        rpmMin,
        Number.isFinite(rpmMax) ? rpmMax : null,
        item.speed,
        item.speedOperator,
      );
      if (!unique.has(key)) {
        unique.set(key, {
          name,
          rpmMax,
          rpmMin,
          speed: speedText(item.speed, item.speedOperator),
          key,
        });
      }
    });

    const sorted = Array.from(unique.values()).sort((a, b) => {
      if (a.rpmMax !== b.rpmMax) return a.rpmMax < b.rpmMax ? -1 : 1;
      return (a.rpmMin ?? 0) - (b.rpmMin ?? 0);
    });

    let prevMax = -1;
    return sorted.map((item) => {
      let rpmRange = '-';
      if (item.rpmMin !== null) {
        rpmRange = Number.isFinite(item.rpmMax)
          ? `${item.rpmMin} - ${item.rpmMax}`
          : `${item.rpmMin}<`;
      } else if (Number.isFinite(item.rpmMax)) {
        const min = prevMax >= 0 ? prevMax + 1 : 0;
        rpmRange = `${min} - ${item.rpmMax}`;
        prevMax = item.rpmMax;
      }
      return { ...item, rpmRange };
    });
  })();

  const summaryModeKey = (name: string) =>
    String(name || '')
      .trim()
      .split(' ')[0]
      .toLowerCase();

  const summaryOrder = (() => {
    if (opModeDetails.length) {
      const order: string[] = [];
      opModeDetails.forEach((item) => {
        const base = summaryModeKey(item.name);
        if (base && !order.includes(base)) {
          order.push(base);
        }
      });
      return order;
    }
    const fallback = new Set<string>();
    if (operationMode) {
      Object.keys(operationMode).forEach((key) => {
        const entry = operationMode?.[key];
        ['port', 'starboard', 'center'].forEach((engine) => {
          operationModeEntries(entry?.[engine]).forEach(
            ({ mode: modeName }) => {
              if (modeName) {
                fallback.add(summaryModeKey(String(modeName)));
              }
            },
          );
        });
      });
    }
    return Array.from(fallback.values());
  })();

  const accumulateOperationMode = (
    engines: string[],
    normalize: (name: string) => string,
  ) => {
    const acc = new Map<string, { runningTime: number; consRate: number }>();
    if (operationMode) {
      Object.keys(operationMode).forEach((key) => {
        if (!isInsideRange(Number(key))) return;
        const entry = operationMode?.[key];
        engines.forEach((engine) => {
          operationModeEntries(entry?.[engine]).forEach((data) => {
            const name = normalize(String(data.mode || '').trim());
            if (!name || name === '-') return;
            if (!acc.has(name)) {
              acc.set(name, { runningTime: 0, consRate: 0 });
            }
            const current = acc.get(name)!;
            current.runningTime += Number(asJson(data.runningTime) || 0);
            current.consRate += Number(asJson(data.consRate) || 0);
          });
        });
      });
    }
    return acc;
  };

  /**
   * One row per tier of the settings, so an idle per speed band keeps a row
   * for each of its ranges. Tiers of different engines that read the same
   * (name and range) share a row. A minute is added to the row of the tier it
   * matched; a reading that carries no tier goes to the first row of its name.
   */
  const detailRows = (() => {
    type Row = {
      name: string;
      rpmRange: string;
      speed: string;
      runningTime: number;
      consRate: number;
    };
    const rows: Row[] = [];
    const rowByTier = new Map<string, Row>();
    const rowByName = new Map<string, Row>();
    const rowByDisplay = new Map<string, Row>();

    opModeDetails.forEach((tier) => {
      const display = `${tier.name.toLowerCase()}__${tier.rpmRange}__${tier.speed}`;
      let row = rowByDisplay.get(display);
      if (!row) {
        row = {
          name: tier.name,
          rpmRange: tier.rpmRange,
          speed: tier.speed,
          runningTime: 0,
          consRate: 0,
        };
        rows.push(row);
        rowByDisplay.set(display, row);
      }
      rowByTier.set(tier.key, row);
      const nameKey = tier.name.toLowerCase();
      if (!rowByName.has(nameKey)) rowByName.set(nameKey, row);
    });

    if (operationMode) {
      Object.keys(operationMode).forEach((key) => {
        if (!isInsideRange(Number(key))) return;
        const entry = operationMode?.[key];
        engineKeys.forEach((engine) => {
          operationModeEntries(entry?.[engine]).forEach((data) => {
            const name = String(data.mode || '').trim();
            if (!name || name === '-') return;
            const nameKey = name.toLowerCase();
            let row =
              rowByTier.get(
                tierKey(
                  name,
                  data.rpmMin,
                  data.rpmMax,
                  data.speed,
                  data.speedOperator,
                ),
              ) || rowByName.get(nameKey);
            if (!row) {
              row = {
                name,
                rpmRange: '-',
                speed: '-',
                runningTime: 0,
                consRate: 0,
              };
              rows.push(row);
              rowByName.set(nameKey, row);
            }
            row.runningTime += Number(asJson(data.runningTime) || 0);
            row.consRate += Number(asJson(data.consRate) || 0);
          });
        });
      });
    }

    return rows.map((row) => ({
      ...row,
      runningTime: row.runningTime / engineCount,
    }));
  })();

  const summaryRows = (() => {
    const acc = accumulateOperationMode(
      engineKeys,
      summaryModeKey,
    );
    return summaryOrder.map((name) => {
      const data = acc.get(name) || { runningTime: 0, consRate: 0 };
      return {
        name,
        runningTime: data.runningTime / engineCount,
        consRate: data.consRate,
      };
    });
  })();

  const clutchModeDetails = (() => {
    const unique = new Map<string, { name: string; clutchMax: number }>();
    clutchOpModes.forEach((item) => {
      const name = String(item.name || '').trim();
      if (!name) return;
      const clutchMax = Number(item.clutchMax);
      if (!Number.isFinite(clutchMax)) return;
      const key = `${name}__${clutchMax}`;
      if (!unique.has(key)) unique.set(key, { name, clutchMax });
    });

    const sorted = Array.from(unique.values()).sort(
      (a, b) => a.clutchMax - b.clutchMax,
    );
    let prevMax = -1;
    return sorted.map((item) => {
      const min = prevMax >= 0 ? prevMax + 1 : 0;
      prevMax = item.clutchMax;
      return {
        ...item,
        clutchRange: `${min} - ${item.clutchMax}`,
      };
    });
  })();

  const clutchSummaryOrder = (() => {
    if (clutchModeDetails.length) {
      const order: string[] = [];
      clutchModeDetails.forEach((item) => {
        const base = summaryModeKey(item.name);
        if (base && !order.includes(base)) order.push(base);
      });
      return order;
    }

    const fallback = new Set<string>();
    const clutchKeys = clutchEngines.map((engine) => clutchKeyByEngine[engine]);
    if (operationMode) {
      Object.keys(operationMode).forEach((key) => {
        if (!isInsideRange(Number(key))) return;
        const entry = operationMode?.[key];
        clutchKeys.forEach((clutchKey) => {
          operationModeEntries(entry?.[clutchKey]).forEach(
            ({ mode: modeName }) => {
              if (modeName && modeName !== '-') {
                fallback.add(summaryModeKey(String(modeName)));
              }
            },
          );
        });
      });
    }
    return Array.from(fallback.values());
  })();

  const clutchSummaryRows = (() => {
    const clutchKeys = clutchEngines.map((engine) => clutchKeyByEngine[engine]);
    const divisor = clutchKeys.length || 1;
    const acc = accumulateOperationMode(
      clutchKeys,
      summaryModeKey,
    );
    return clutchSummaryOrder.map((name) => {
      const data = acc.get(name) || { runningTime: 0, consRate: 0 };
      return {
        name,
        runningTime: data.runningTime / divisor,
        consRate: data.consRate,
      };
    });
  })();

  const clutchDetailRows = (() => {
    const clutchKeys = clutchEngines.map((engine) => clutchKeyByEngine[engine]);
    const divisor = clutchKeys.length || 1;
    const order = clutchModeDetails.length
      ? clutchModeDetails.map((item) => item.name)
      : clutchSummaryOrder;
    const acc = accumulateOperationMode(clutchKeys, (name) => name);
    return order.map((name) => {
      const found = clutchModeDetails.find((item) => item.name === name);
      const data = acc.get(name) || { runningTime: 0, consRate: 0 };
      return {
        name,
        clutchRange: found?.clutchRange || '-',
        runningTime: data.runningTime / divisor,
        consRate: data.consRate,
      };
    });
  })();

  const perEngineClutchRows = (() => {
    const result: Record<string, OperationModeSummaryRow[]> = {};
    clutchEngines.forEach((engine) => {
      const acc = accumulateOperationMode(
        [clutchKeyByEngine[engine]],
        summaryModeKey,
      );
      result[engine] = clutchSummaryOrder.map((name) => {
        const data = acc.get(name) || { runningTime: 0, consRate: 0 };
        return {
          name,
          runningTime: data.runningTime,
          consRate: data.consRate,
        };
      });
    });
    return result;
  })();

  const perEngineRows = (() => {
    const result: Record<string, OperationModeSummaryRow[]> = {};
    engineKeys.forEach((engine) => {
      const acc = accumulateOperationMode(
        [engine],
        summaryModeKey,
      );
      result[engine] = summaryOrder.map((name) => {
        const data = acc.get(name) || { runningTime: 0, consRate: 0 };
        return {
          name,
          runningTime: data.runningTime,
          consRate: data.consRate,
        };
      });
    });
    return result;
  })();

  const hasData =
    operationMode &&
    Object.keys(operationMode).length &&
    (engineKeys.length > 0 || clutchEngines.length > 0);

  const perEngineHasData = (() => {
    if (!hasData) return false;
    return engineKeys.some((engine) => {
      const rows = perEngineRows[engine] || [];
      return rows.some((row) => row.runningTime || row.consRate);
    });
  })();

  return {
    engineKeys,
    clutchEngines,
    summaryRows,
    detailRows,
    perEngineRows,
    perEngineHasData,
    clutchSummaryRows,
    clutchDetailRows,
    perEngineClutchRows,
  };
}

/* -------------------------------------------------------------------------- */
/* vessel tracking                                                             */
/* -------------------------------------------------------------------------- */

export interface VesselTrackingPoint {
  latitude: number;
  longitude: number;
  timestamp: number;
}

export interface VesselTrackingData {
  currentPath: Array<[number, number]>;
  first: VesselTrackingPoint | null;
  last: VesselTrackingPoint | null;
}

/**
 * Hourly points of the route and the first and last position. The page
 * turns the positions into degrees, minutes and seconds and the timestamps
 * into its local time.
 */
export function buildVesselTrackingData(
  gpsLogs?: Record<string, any> | null,
): VesselTrackingData {
  const currentPath: Array<[number, number]> = [];
  let first: VesselTrackingPoint | null = null;
  let last: VesselTrackingPoint | null = null;

  if (gpsLogs && Object.keys(gpsLogs).length > 0) {
    Object.values(gpsLogs).forEach((item: any) => {
      if (
        asJson(item.latitude) &&
        asJson(item.longitude) &&
        asJson(item.timestamp)
      ) {
        if (item.timestamp % 3600 === 0) {
          currentPath.push([item.latitude, item.longitude]);
        }
      }
    });

    const keys = Object.keys(gpsLogs);
    keys.forEach((key, idx) => {
      const currentGps = gpsLogs[key];

      if (idx === 0) {
        first = {
          latitude:
            asJson(currentGps.stats?.firstLatitude) ??
            asJson(currentGps.latitude),
          longitude:
            asJson(currentGps.stats?.firstLongitude) ??
            asJson(currentGps.longitude),
          timestamp: Number(key),
        };
      }

      if (idx === keys.length - 1) {
        last = {
          latitude:
            asJson(currentGps.stats?.lastLatitude) ??
            asJson(currentGps.latitude),
          longitude:
            asJson(currentGps.stats?.lastLongitude) ??
            asJson(currentGps.longitude),
          timestamp: Number(key),
        };
      }
    });
  }

  return { currentPath, first, last };
}

/* -------------------------------------------------------------------------- */
/* response of GET /report-log/pdf-data                                        */
/* -------------------------------------------------------------------------- */

export interface ReportPdfData {
  // false: the page shows that something is wrong, as with an empty log
  hasLogs: boolean;
  // generateReportSummary / calculateGpsSummary
  summary: Record<string, any>;
  gpsSummary: { avgSpeed: string; totalCruise: string; totalDistance: string };
  units: { speedUnit: string; distanceUnit: string; cruiseUnit: string };
  charts: {
    rpmFuelSpeed?: PdfChartData;
    // by chart type: me, port, starboard, center
    fuelCons: Record<string, PdfChartData>;
    aeRunningTime?: PdfChartData;
  };
  operationMode?: OperationModeSections;
  vesselTracking?: VesselTrackingData;
  // metadata.showOperationMode of the asset, which the page cannot read with
  // its temporary token
  showOperationMode?: boolean;
}
